import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  Button,
  Image,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  List,
  ListItem,
  ListIcon,
  Link,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronRightIcon, CheckIcon } from "@chakra-ui/icons";
import { FaServer, FaShieldAlt, FaUsers, FaCalendarAlt, FaShoppingCart, FaFileInvoiceDollar, FaTasks, FaBitcoin, FaLink, FaCode, FaLayerGroup, FaHandshake, FaMobileAlt, FaEnvelope, FaBullhorn, FaVideo, FaCoins, FaRobot, FaBrain, FaCogs, FaChartLine, FaPhone, FaFacebook, FaInstagram, FaQuoteLeft, FaStar, FaPlay } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../brandConfig";
import { VapiFloatingCallButton } from "../components/VapiFloatingCallButton";

const Home = () => {
  const navigate = useNavigate();
  const { isOpen: isVideoOpen, onOpen: onVideoOpen, onClose: onVideoClose } = useDisclosure();

  // Thunder Compute-style gradient background for cards
  const cardGradientBg = `linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)`;
  const cardBorder = "rgba(255, 255, 255, 0.1)";
  const textPrimary = "#FFFFFF";
  const textSecondary = "rgba(255, 255, 255, 0.7)";
  const textMuted = "rgba(255, 255, 255, 0.5)";
  const accentBlue = "#369eff";

  const handleCallAssistant = () => {
    window.location.href = "tel:+61468003978";
  };

  return (
    <>
      {/* Navbar - Outside the main container for proper sticky positioning */}
      <NavbarWithCallToAction />

      <Box
        bg={getColor("background.main")}
        minHeight="100vh"
        position="relative"
        overflow="hidden"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
          radial-gradient(60% 60% at 0% 0%, #112334 0%, transparent 50%),
          radial-gradient(50% 50% at 50% 50%, #369eff33 0%, transparent 100%)
        `,
          pointerEvents: 'none',
          zIndex: 0,
        }}
        sx={{
          '@keyframes snowfall': {
            '0%': {
              transform: 'translateX(-200px) translateY(-100px)',
              opacity: 0,
            },
            '5%': {
              opacity: 0.4,
            },
            '25%': {
              transform: 'translateX(25vw) translateY(25vh)',
              opacity: 0.8,
            },
            '50%': {
              transform: 'translateX(50vw) translateY(50vh)',
              opacity: 1,
            },
            '75%': {
              transform: 'translateX(75vw) translateY(75vh)',
              opacity: 0.8,
            },
            '95%': {
              opacity: 0.4,
            },
            '100%': {
              transform: 'translateX(calc(100vw + 200px)) translateY(100vh)',
              opacity: 0,
            }
          },
          '@keyframes snowfall-slow': {
            '0%': {
              transform: 'translateX(-250px) translateY(-100px)',
              opacity: 0,
            },
            '5%': {
              opacity: 0.3,
            },
            '30%': {
              transform: 'translateX(35vw) translateY(30vh)',
              opacity: 0.6,
            },
            '60%': {
              transform: 'translateX(65vw) translateY(60vh)',
              opacity: 0.8,
            },
            '95%': {
              opacity: 0.3,
            },
            '100%': {
              transform: 'translateX(calc(100vw + 250px)) translateY(100vh)',
              opacity: 0,
            }
          },
          '@keyframes snowfall-medium': {
            '0%': {
              transform: 'translateX(-180px) translateY(-100px)',
              opacity: 0,
            },
            '8%': {
              opacity: 0.5,
            },
            '40%': {
              transform: 'translateX(45vw) translateY(40vh)',
              opacity: 0.9,
            },
            '70%': {
              transform: 'translateX(80vw) translateY(70vh)',
              opacity: 0.7,
            },
            '92%': {
              opacity: 0.4,
            },
            '100%': {
              transform: 'translateX(calc(100vw + 180px)) translateY(100vh)',
              opacity: 0,
            }
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-10%',
            width: '120%',
            height: '100%',
            backgroundImage: `
            radial-gradient(3px 3px at 10% 10%, rgba(255,255,255,0.6) 50%, transparent 50%),
            radial-gradient(4px 4px at 20% 30%, rgba(255,255,255,0.5) 50%, transparent 50%),
            radial-gradient(2px 2px at 15% 50%, rgba(255,255,255,0.7) 50%, transparent 50%),
            radial-gradient(5px 5px at 25% 20%, rgba(255,255,255,0.4) 50%, transparent 50%),
            radial-gradient(3px 3px at 30% 60%, rgba(255,255,255,0.6) 50%, transparent 50%),
            radial-gradient(6px 6px at 35% 40%, rgba(255,255,255,0.3) 50%, transparent 50%),
            radial-gradient(4px 4px at 40% 70%, rgba(255,255,255,0.5) 50%, transparent 50%),
            radial-gradient(2px 2px at 45% 15%, rgba(255,255,255,0.8) 50%, transparent 50%),
            radial-gradient(5px 5px at 50% 80%, rgba(255,255,255,0.4) 50%, transparent 50%)
          `,
            backgroundSize: '400px 400px, 500px 500px, 350px 350px, 450px 450px, 550px 550px, 600px 600px, 480px 480px, 420px 420px, 520px 520px',
            backgroundPosition: '0 0, 80px 60px, 160px 40px, 240px 100px, 320px 160px, 400px 80px, 480px 200px, 560px 20px, 640px 180px',
            animation: 'snowfall 25s linear infinite',
            pointerEvents: 'none',
            zIndex: 1,
          }
        }}
      >
        {/* Snow effect layers */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          pointerEvents="none"
          zIndex={1}
        >
          {/* Second snow layer - slower and larger */}
          <Box
            position="absolute"
            top={0}
            left="-20%"
            width="140%"
            height="100%"
            opacity={0.6}
            sx={{
              backgroundImage: `
              radial-gradient(5px 5px at 5% 15%, rgba(255,255,255,0.4) 50%, transparent 50%),
              radial-gradient(3px 3px at 15% 35%, rgba(255,255,255,0.5) 50%, transparent 50%),
              radial-gradient(7px 7px at 10% 55%, rgba(255,255,255,0.3) 50%, transparent 50%),
              radial-gradient(4px 4px at 20% 25%, rgba(255,255,255,0.6) 50%, transparent 50%),
              radial-gradient(8px 8px at 25% 45%, rgba(255,255,255,0.25) 50%, transparent 50%),
              radial-gradient(6px 6px at 30% 65%, rgba(255,255,255,0.45) 50%, transparent 50%),
              radial-gradient(3px 3px at 35% 20%, rgba(255,255,255,0.7) 50%, transparent 50%)
            `,
              backgroundSize: '600px 600px, 700px 700px, 550px 550px, 650px 650px, 750px 750px, 680px 680px, 580px 580px',
              backgroundPosition: '0 0, 120px 140px, 240px 80px, 360px 200px, 480px 280px, 600px 120px, 720px 40px',
              animation: 'snowfall-slow 35s linear infinite',
            }}
          />

          {/* Third snow layer - medium speed */}
          <Box
            position="absolute"
            top={0}
            left="-15%"
            width="130%"
            height="100%"
            opacity={0.5}
            sx={{
              backgroundImage: `
              radial-gradient(4px 4px at 8% 20%, rgba(255,255,255,0.5) 50%, transparent 50%),
              radial-gradient(6px 6px at 18% 40%, rgba(255,255,255,0.35) 50%, transparent 50%),
              radial-gradient(3px 3px at 12% 60%, rgba(255,255,255,0.6) 50%, transparent 50%),
              radial-gradient(5px 5px at 22% 30%, rgba(255,255,255,0.4) 50%, transparent 50%),
              radial-gradient(7px 7px at 28% 50%, rgba(255,255,255,0.3) 50%, transparent 50%),
              radial-gradient(4px 4px at 32% 70%, rgba(255,255,255,0.55) 50%, transparent 50%)
            `,
              backgroundSize: '550px 550px, 650px 650px, 500px 500px, 600px 600px, 700px 700px, 580px 580px',
              backgroundPosition: '50px 30px, 150px 150px, 250px 90px, 350px 210px, 450px 270px, 550px 150px',
              animation: 'snowfall-medium 30s linear infinite',
            }}
          />
        </Box>

        {/* Hero Section with Video Background - Full Width */}
        <Box
          position="relative"
          width="100vw"
          height="624px"
          overflow="hidden"
          marginLeft="50%"
          transform="translateX(-50%)"
        >
          {/* Video Background */}
          <Box
            as="video"
            autoPlay
            muted
            loop
            playsInline
            position="absolute"
            top="50%"
            left="50%"
            minWidth="100%"
            minHeight="100%"
            width="auto"
            height="auto"
            transform="translate(-50%, -50%)"
            objectFit="cover"
            zIndex={0}
          >
            <source src="/assets/onesolarimages/Inro-One-Solar-2021.mp4" type="video/mp4" />
          </Box>

          {/* Dark Overlay */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(0, 0, 0, 0.5)"
            zIndex={1}
          />

          {/* Hero Content */}
          <Container
            maxW="1200px"
            position="relative"
            zIndex={2}
            height="100%"
          >
            {/* Mobile Social Icons at Top */}
            <HStack
              spacing={4}
              position="absolute"
              top={8}
              left="50%"
              transform="translateX(-50%)"
              display={{ base: "flex", md: "none" }}
              zIndex={3}
            >
              <Link
                href="https://www.facebook.com/share/14NK1QCPNc1/?mibextid=wwXIfr"
                target="_blank"
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="40px"
                h="40px"
                borderRadius="full"
                bg="rgba(255, 255, 255, 0.1)"
                color="white"
                _hover={{
                  bg: "rgba(255, 255, 255, 0.2)",
                  transform: "scale(1.1)"
                }}
                transition="all 0.3s"
              >
                <Icon as={FaFacebook} w={5} h={5} />
              </Link>
              <Link
                href="https://www.instagram.com/one_solar_nz?igsh=MXRlZDZ1Mmx2dnByeA%3D%3D&utm_source=qr"
                target="_blank"
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="40px"
                h="40px"
                borderRadius="full"
                bg="rgba(255, 255, 255, 0.1)"
                color="white"
                _hover={{
                  bg: "rgba(255, 255, 255, 0.2)",
                  transform: "scale(1.1)"
                }}
                transition="all 0.3s"
              >
                <Icon as={FaInstagram} w={5} h={5} />
              </Link>
            </HStack>

            <Flex
              height="100%"
              align="center"
              direction="row"
              gap={8}
            >
              {/* Left Column - Social Icons for Desktop (20%) */}
              <Box
                width={{ base: "10%", md: "20%" }}
                display={{ base: "none", md: "flex" }}
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                gap={4}
              >
                <VStack spacing={4}>
                  <Link
                    href="https://www.facebook.com/share/14NK1QCPNc1/?mibextid=wwXIfr"
                    target="_blank"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="rgba(255, 255, 255, 0.1)"
                    color="white"
                    _hover={{
                      bg: "rgba(255, 255, 255, 0.2)",
                      transform: "scale(1.1)"
                    }}
                    transition="all 0.3s"
                  >
                    <Icon as={FaFacebook} w={5} h={5} />
                  </Link>
                  <Link
                    href="https://www.instagram.com/one_solar_nz?igsh=MXRlZDZ1Mmx2dnByeA%3D%3D&utm_source=qr"
                    target="_blank"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="rgba(255, 255, 255, 0.1)"
                    color="white"
                    _hover={{
                      bg: "rgba(255, 255, 255, 0.2)",
                      transform: "scale(1.1)"
                    }}
                    transition="all 0.3s"
                  >
                    <Icon as={FaInstagram} w={5} h={5} />
                  </Link>
                </VStack>
              </Box>

              {/* Right Column - Main Content (80%) */}
              <Box
                width={{ base: "90%", md: "80%" }}
                textAlign={{ base: "left", md: "left" }}
                color="white"
              >
                <Heading
                  as="h1"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="400"
                  mb={6}
                  letterSpacing="wide"
                  textTransform="uppercase"
                  textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                  position="relative"
                  textAlign={{ base: "left", md: "left" }}
                  fontFamily="'Open Sans', sans-serif"
                  _after={{
                    content: '""',
                    position: 'absolute',
                    bottom: '-12px',
                    left: '0',
                    width: '80px',
                    height: '4px',
                    background: 'white',
                    borderRadius: '2px'
                  }}
                >
                  YOUR NEW ZEALAND SOLAR SPECIALISTS
                </Heading>

                <Heading
                  as="h2"
                  fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                  fontWeight="bold"
                  mb={8}
                  lineHeight="1.2"
                  textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                  fontFamily="'Open Sans', sans-serif"
                >
                  ONE SOLAR,<br />THE SMART CHOICE
                </Heading>

                <HStack
                  spacing={4}
                  justify="flex-start"
                  flexWrap="wrap"
                  display={{ base: "none", md: "flex" }}
                >
                  <Button
                    size="lg"
                    bg="#121c47"
                    color="white"
                    px={8}
                    py={6}
                    fontSize="md"
                    fontWeight="600"
                    textTransform="uppercase"
                    borderRadius="4px"
                    fontFamily="'Open Sans', sans-serif"
                    _hover={{
                      bg: "#0a1230",
                      transform: "translateY(-2px)",
                      boxShadow: "lg"
                    }}
                    transition="all 0.3s"
                    onClick={() => navigate("/contact")}
                  >
                    Get Free Quote
                  </Button>

                  <Button
                    size="lg"
                    bg="transparent"
                    color="white"
                    border="2px solid white"
                    px={8}
                    py={6}
                    fontSize="md"
                    fontWeight="600"
                    textTransform="uppercase"
                    borderRadius="4px"
                    fontFamily="'Open Sans', sans-serif"
                    _hover={{
                      bg: "rgba(255, 255, 255, 0.1)",
                      transform: "translateY(-2px)",
                      boxShadow: "lg"
                    }}
                    transition="all 0.3s"
                    onClick={() => navigate("/contact")}
                  >
                    Contact Us
                  </Button>
                </HStack>

                {/* Mobile Buttons - Full Width at Bottom */}
                <VStack
                  spacing={3}
                  width="100%"
                  mt={8}
                  display={{ base: "flex", md: "none" }}
                  position="absolute"
                  bottom={8}
                  left={0}
                  right={0}
                  px={4}
                >
                  <Button
                    width="100%"
                    size="lg"
                    bg="#121c47"
                    color="white"
                    py={6}
                    fontSize="md"
                    fontWeight="600"
                    textTransform="uppercase"
                    borderRadius="4px"
                    fontFamily="'Open Sans', sans-serif"
                    _hover={{
                      bg: "#0a1230",
                      transform: "translateY(-2px)",
                      boxShadow: "lg"
                    }}
                    transition="all 0.3s"
                    onClick={() => navigate("/contact")}
                  >
                    Get Free Quote
                  </Button>

                  <Button
                    width="100%"
                    size="lg"
                    bg="transparent"
                    color="white"
                    border="2px solid white"
                    py={6}
                    fontSize="md"
                    fontWeight="600"
                    textTransform="uppercase"
                    borderRadius="4px"
                    fontFamily="'Open Sans', sans-serif"
                    _hover={{
                      bg: "rgba(255, 255, 255, 0.1)",
                      transform: "translateY(-2px)",
                      boxShadow: "lg"
                    }}
                    transition="all 0.3s"
                    onClick={() => navigate("/contact")}
                  >
                    Contact Us
                  </Button>
                </VStack>
              </Box>
            </Flex>
          </Container>
        </Box>

        {/* Trusted Installer Section */}
        <Box
          bg="white"
          pt={{ base: 16, md: 24 }}
          pb={{ base: 8, md: 12 }}
          position="relative"
        >
          <Container maxW="1200px">
            <Box textAlign="left">
              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color="#333"
                lineHeight="1.2"
                textTransform="uppercase"
                fontFamily="'Open Sans', sans-serif"
              >
                We are New Zealand's Most Trusted Solar Installer.
              </Heading>
            </Box>
          </Container>
        </Box>

        {/* Customer Reviews Section */}
        <Box
          bg="white"
          pt={{ base: 4, md: 8 }}
          pb={{ base: 16, md: 24 }}
          position="relative"
        >
          <Container maxW="1200px">
            <Box>
              <Box
                width="50px"
                height="4px"
                bg="#121c47"
                borderRadius="2px"
                mb={4}
              />

              <Text
                fontSize="md"
                fontWeight="500"
                textTransform="uppercase"
                letterSpacing="wide"
                color="gray.600"
                mb={4}
                fontFamily="'Open Sans', sans-serif"
              >
                what our customers say
              </Text>

              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                color="#333"
                mb={8}
                lineHeight="1.2"
                fontFamily="'Open Sans', sans-serif"
              >
                SEE OUR AMAZING REVIEWS below.
              </Heading>

              {/* Customer Reviews */}
              <VStack spacing={6} align="stretch">
                {/* Review 1 */}
                <Box
                  bg="white"
                  borderRadius="lg"
                  p={6}
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                  _hover={{ boxShadow: "md" }}
                  transition="all 0.3s"
                >
                  <HStack spacing={1} mb={3}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Icon key={star} as={FaStar} w={5} h={5} color="#FFD700" />
                    ))}
                  </HStack>
                  <Text fontWeight="bold" mb={2} fontFamily="'Open Sans', sans-serif">
                    Sarah Mitchell - Hamilton
                  </Text>
                  <Text color="gray.600" fontFamily="'Open Sans', sans-serif">
                    "One Solar New Zealand made the entire process seamless! From the initial consultation to installation,
                    their team was professional and knowledgeable. Our electricity bills have dropped by 80% and the
                    system looks great on our roof. Highly recommend!"
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={2} fontFamily="'Open Sans', sans-serif">
                    Verified Customer - 2 weeks ago
                  </Text>
                </Box>

                {/* Review 2 */}
                <Box
                  bg="white"
                  borderRadius="lg"
                  p={6}
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                  _hover={{ boxShadow: "md" }}
                  transition="all 0.3s"
                >
                  <HStack spacing={1} mb={3}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Icon key={star} as={FaStar} w={5} h={5} color="#FFD700" />
                    ))}
                  </HStack>
                  <Text fontWeight="bold" mb={2} fontFamily="'Open Sans', sans-serif">
                    Mark Thompson - Auckland
                  </Text>
                  <Text color="gray.600" fontFamily="'Open Sans', sans-serif">
                    "Best decision we've made for our home! The team at One Solar helped us choose the perfect
                    system for our needs and the financing options made it very affordable. Installation was
                    completed in one day with minimal disruption. 5 stars!"
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={2} fontFamily="'Open Sans', sans-serif">
                    Verified Customer - 1 month ago
                  </Text>
                </Box>

                {/* Review 3 */}
                <Box
                  bg="white"
                  borderRadius="lg"
                  p={6}
                  border="1px solid"
                  borderColor="gray.200"
                  boxShadow="sm"
                  _hover={{ boxShadow: "md" }}
                  transition="all 0.3s"
                >
                  <HStack spacing={1} mb={3}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Icon key={star} as={FaStar} w={5} h={5} color="#FFD700" />
                    ))}
                  </HStack>
                  <Text fontWeight="bold" mb={2} fontFamily="'Open Sans', sans-serif">
                    Lisa Chen - Wellington
                  </Text>
                  <Text color="gray.600" fontFamily="'Open Sans', sans-serif">
                    "Exceptional service from start to finish! One Solar's team explained everything clearly,
                    handled all the paperwork, and the quality of the installation is outstanding. Already
                    seeing significant savings on our power bills. Thank you One Solar!"
                  </Text>
                  <Text fontSize="sm" color="gray.500" mt={2} fontFamily="'Open Sans', sans-serif">
                    Verified Customer - 2 months ago
                  </Text>
                </Box>

                {/* Overall Rating */}
                <Box
                  textAlign="center"
                  py={4}
                  borderTop="1px solid"
                  borderColor="gray.200"
                >
                  <HStack justify="center" spacing={1} mb={2}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Icon key={star} as={FaStar} w={6} h={6} color="#FFD700" />
                    ))}
                  </HStack>
                  <Text fontWeight="bold" fontSize="lg" fontFamily="'Open Sans', sans-serif">
                    4.9 out of 5 stars
                  </Text>

                </Box>
              </VStack>
            </Box>
          </Container>
        </Box>

        {/* Flexible Financing Section */}
        <Box
          bg="white"
          py={{ base: 16, md: 24 }}
          position="relative"
        >
          <Container maxW="1200px">
            <Flex
              direction={{ base: "column", md: "row" }}
              gap={8}
              align="center"
            >
              {/* Left Column - Image */}
              <Box
                width={{ base: "100%", md: "50%" }}
              >
                <Image
                  src="/assets/onesolarimages/smartfinancingimage.webp"
                  alt="Solar power with smart financing"
                  borderRadius="lg"
                  width="100%"
                  height="auto"
                  objectFit="cover"
                />
              </Box>

              {/* Right Column - Content */}
              <Box
                width={{ base: "100%", md: "50%" }}
                textAlign={{ base: "center", md: "left" }}
              >
                <Box
                  width="50px"
                  height="4px"
                  bg="#121c47"
                  borderRadius="2px"
                  mb={4}
                  mx={{ base: "auto", md: "0" }}
                />

                <Text
                  fontSize="md"
                  fontWeight="500"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  color="gray.600"
                  mb={4}
                  fontFamily="'Open Sans', sans-serif"
                >
                  FLEXIBLE FINANCING AVAILABLE
                </Text>

                <Heading
                  as="h2"
                  fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                  fontWeight="bold"
                  color="#333"
                  mb={6}
                  lineHeight="1.2"
                  fontFamily="'Open Sans', sans-serif"
                >
                  Solar power with smart financing.
                </Heading>

                <Text
                  fontSize="lg"
                  color="gray.600"
                  mb={8}
                  fontFamily="'Open Sans', sans-serif"
                >
                  One Solar New Zealand offers competitive solar loans and financing options that make clean energy affordable for everyone.
                </Text>

                <Button
                  size="lg"
                  bg="#121c47"
                  color="white"
                  px={8}
                  py={6}
                  fontSize="md"
                  fontWeight="600"
                  textTransform="uppercase"
                  borderRadius="4px"
                  fontFamily="'Open Sans', sans-serif"
                  _hover={{
                    bg: "#0a1230",
                    transform: "translateY(-2px)",
                    boxShadow: "lg"
                  }}
                  transition="all 0.3s"
                  onClick={() => window.open("https://www.ausloans.com.au/partner/onefinance", "_blank")}
                >
                  GET FINANCE
                </Button>
              </Box>
            </Flex>
          </Container>
        </Box>

        {/* About One Solar Section */}
        <Box
          bg="#121c47"
          position="relative"
          width="100%"
        >
          <Flex
            direction={{ base: "column", md: "row" }}
            minHeight={{ base: "auto", md: "600px" }}
          >
            {/* Left Column - Image Background with Text */}
            <Box
              width={{ base: "100%", md: "50%" }}
              position="relative"
              minHeight={{ base: "300px", md: "600px" }}
              backgroundImage="url('/assets/onesolarimages/0002_solar-repairs-professional.jpg')"
              backgroundSize="cover"
              backgroundPosition="center"
              backgroundRepeat="no-repeat"
            >
              {/* Dark Overlay */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                bg="rgba(18, 28, 71, 0.7)"
              />

              {/* Content */}
              <Box
                position="relative"
                zIndex={2}
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                p={{ base: 8, md: 12 }}
              >
                <Heading
                  as="h4"
                  fontSize={{ base: "40px", md: "60px", lg: "80px" }}
                  fontWeight="900"
                  color="white"
                  textAlign="left"
                  textTransform="uppercase"
                  fontFamily="'Open Sans', sans-serif"
                  lineHeight="1.1"
                >
                  Start slashing your power bills
                </Heading>
              </Box>
            </Box>

            {/* Right Column - Content */}
            <Box
              width={{ base: "100%", md: "50%" }}
              bg="#121c47"
              p={{ base: 8, md: 12 }}
              display="flex"
              alignItems="center"
            >
              <Box>
                <Box
                  width="50px"
                  height="4px"
                  bg="white"
                  borderRadius="2px"
                  mb={4}
                />

                <Text
                  fontSize="md"
                  fontWeight="bold"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  color="rgba(255, 255, 255, 0.9)"
                  mb={6}
                  fontFamily="'Open Sans', sans-serif"
                >
                  ABOUT ONE SOLAR NEW ZEALAND
                </Text>

                <VStack spacing={4} align="stretch">
                  <Text color="white" fontSize="md" fontFamily="'Open Sans', sans-serif">
                    From our Energy Advisors to our installers, every member of our team is a seasoned expert dedicated to providing you with the utmost level of advice and workmanship. We prioritize understanding your household's specific requirements to ensure you make an informed investment in the ideal solar power system.
                  </Text>

                  <Text color="white" fontSize="md" fontFamily="'Open Sans', sans-serif">
                    Our Energy Advisors possess extensive knowledge and will guide you through the entire process, addressing any questions or concerns you may have along the way. Our installers are skilled professionals who will ensure the installation is executed to the highest standards of quality and efficiency.
                  </Text>

                  <Box bg="rgba(255, 255, 255, 0.1)" p={4} borderRadius="md">
                    <Text color="white" fontSize="md" fontWeight="600" fontFamily="'Open Sans', sans-serif">
                      Experience a significant reduction in your energy bills for the long term, while having the peace of mind that you have partnered with the right team to generate your own power.
                    </Text>
                  </Box>

                  <Text color="white" fontSize="md" fontFamily="'Open Sans', sans-serif">
                    At One Solar, we exclusively recommend, provide, and install solar panels, inverters, and monitoring systems of the highest quality. We understand the importance of reliable and efficient equipment to maximize the benefits of solar energy.
                  </Text>

                  <Text color="white" fontSize="md" fontFamily="'Open Sans', sans-serif">
                    Take the first step towards a greener and more cost-effective future by requesting your obligation-free solar quote today. Our team will provide you with all the necessary information to help you make an informed decision about harnessing the power of solar energy for your home.
                  </Text>
                </VStack>
              </Box>
            </Box>
          </Flex>
        </Box>

        {/* 3 Simple Steps Section */}
        <Box
          bg="white"
          pt={{ base: 20, md: 32 }}
          pb={{ base: 10, md: 16 }}
          position="relative"
          width="100%"
        >
          <Container maxW="1200px">
            <Box textAlign="center" mb={16}>
              <Heading
                as="h2"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight="bold"
                color="#050303"
                lineHeight="1.2"
                textTransform="uppercase"
                fontFamily="'Open Sans', sans-serif"
              >
                3 Simple Steps To Save You Thousands
              </Heading>
            </Box>

            {/* Steps Grid */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              {/* Step 1 */}
              <Box
                border="1px solid"
                borderColor="gray.300"
                borderRadius="lg"
                p={6}
                bg="white"
                transition="all 0.3s"
                _hover={{
                  boxShadow: "lg",
                  transform: "translateY(-4px)",
                }}
              >
                <VStack spacing={4} align="center" textAlign="center">
                  {/* Image Placeholder */}
                  <Image
                    src="/assets/onesolarimages/step1.webp"
                    alt="Step 1: Assess"
                    w="100%"
                    maxH="88px"
                    objectFit="contain"
                    borderRadius="md"
                    mb={4}
                  />

                  <Heading
                    as="h3"
                    fontSize="xl"
                    fontWeight="bold"
                    color="#050303"
                    mb={2}
                    fontFamily="'Open Sans', sans-serif"
                  >
                    STEP 1: ASSESS
                  </Heading>
                  <Text
                    color="gray.600"
                    fontSize="md"
                    fontFamily="'Open Sans', sans-serif"
                  >
                    One of our team members will personally visit your home to assess the condition of your roof and meter box.
                    During this visit, they will also have a conversation with you about your current energy bills and energy provider.
                    This allows us to tailor your solar specifically to your needs.
                  </Text>
                </VStack>
              </Box>

              {/* Step 2 */}
              <Box
                border="1px solid"
                borderColor="gray.300"
                borderRadius="lg"
                p={6}
                bg="white"
                transition="all 0.3s"
                _hover={{
                  boxShadow: "lg",
                  transform: "translateY(-4px)",
                }}
              >
                <VStack spacing={4} align="center" textAlign="center">
                  {/* Image Placeholder */}
                  <Image
                    src="/assets/onesolarimages/step2.png"
                    alt="Step 2: Install"
                    w="100%"
                    maxH="88px"
                    objectFit="contain"
                    borderRadius="md"
                    mb={4}
                  />

                  <Heading
                    as="h3"
                    fontSize="xl"
                    fontWeight="bold"
                    color="#050303"
                    mb={2}
                    fontFamily="'Open Sans', sans-serif"
                  >
                    STEP 2: EDUCATE
                  </Heading>
                  <Text
                    color="gray.600"
                    fontSize="md"
                    fontFamily="'Open Sans', sans-serif"
                  >
                    We will provide you with detailed information about various products, options, and prices that are tailored to your home and lifestyle.
                    Our goal is to guide you towards the optimal choices that maximize your energy bill savings.
                  </Text>
                </VStack>
              </Box>

              {/* Step 3 */}
              <Box
                border="1px solid"
                borderColor="gray.300"
                borderRadius="lg"
                p={6}
                bg="white"
                transition="all 0.3s"
                _hover={{
                  boxShadow: "lg",
                  transform: "translateY(-4px)",
                }}
              >
                <VStack spacing={4} align="center" textAlign="center">
                  {/* Image Placeholder */}
                  <Image
                    src="/assets/onesolarimages/step3.png"
                    alt="Step 3: Enjoy"
                    w="100%"
                    maxH="88px"
                    objectFit="contain"
                    borderRadius="md"
                    mb={4}
                  />

                  <Heading
                    as="h3"
                    fontSize="xl"
                    fontWeight="bold"
                    color="#050303"
                    mb={2}
                    fontFamily="'Open Sans', sans-serif"
                  >
                    STEP 3: SAVING
                  </Heading>
                  <Text
                    color="gray.600"
                    fontSize="md"
                    fontFamily="'Open Sans', sans-serif"
                  >
                    Upon the successful installation and connection of your solar system, you will immediately commence enjoying the advantages of savings,
                    starting from the very first day.
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>
          </Container>
        </Box>

        {/* About One Solar Australia Section */}
        <Box
          bg="white"
          pt={{ base: 10, md: 16 }}
          pb={{ base: 10, md: 16 }}
          position="relative"
          width="100%"
        >
          <Container maxW="1200px">
            {/* Section Title */}
            <Box textAlign="left" mb={12}>
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="600"
                textTransform="uppercase"
                letterSpacing="wider"
                color="gray.600"
                fontFamily="'Open Sans', sans-serif"
              >
                ABOUT ONE SOLAR NEW ZEALAND
              </Text>
            </Box>

            {/* Content Grid */}
            <Flex gap={6} flexDirection={{ base: "column", lg: "row" }}>
              {/* Family Image - Wider column */}
              <Box
                flex={{ base: "1", lg: "2" }}
                bg="white"
                p={2}
                borderRadius="md"
                boxShadow="md"
              >
                <Image
                  src="/assets/onesolarimages/family.jpg"
                  alt="One Solar Australia Family"
                  width="100%"
                  height="240px"
                  objectFit="cover"
                  borderRadius="sm"
                />
              </Box>

              {/* Mission Box */}
              <Box
                flex="1"
                bg="white"
                color="#050303"
                p={6}
                borderRadius="lg"
              >
                <Heading
                  as="h3"
                  fontSize="xl"
                  fontWeight="bold"
                  mb={4}
                  fontFamily="'Open Sans', sans-serif"
                >
                  MISSION
                </Heading>
                <Text
                  fontSize="md"
                  fontFamily="'Open Sans', sans-serif"
                >
                  Our mission is to provide the best solutions and excellent service for New Zealanders seeking solar power.
                </Text>
              </Box>

              {/* Vision Box */}
              <Box
                flex="1"
                bg="white"
                color="#050303"
                p={6}
                borderRadius="lg"
              >
                <Heading
                  as="h3"
                  fontSize="xl"
                  fontWeight="bold"
                  mb={4}
                  fontFamily="'Open Sans', sans-serif"
                >
                  VISION
                </Heading>
                <Text
                  fontSize="md"
                  fontFamily="'Open Sans', sans-serif"
                >
                  We are committed to providing you with the best Quality Solar products and service available in New Zealand.
                </Text>
              </Box>

              {/* Technology Box */}
              <Box
                flex="1"
                bg="white"
                color="#050303"
                p={6}
                borderRadius="lg"
              >
                <Heading
                  as="h3"
                  fontSize="xl"
                  fontWeight="bold"
                  mb={4}
                  fontFamily="'Open Sans', sans-serif"
                >
                  TECHNOLOGY
                </Heading>
                <Text
                  fontSize="md"
                  fontFamily="'Open Sans', sans-serif"
                >
                  We only supply the best and quality products on the market ensuring you maximise your investment.
                </Text>
              </Box>
            </Flex>
          </Container>
        </Box>

        {/* Video Testimonials Section - COMMENTED OUT */}
        {/* <Box
        bg="white"
        pt={{ base: 10, md: 16 }}
        pb={{ base: 20, md: 32 }}
        position="relative"
        width="100%"
      >
        <Container maxW="1200px">
          <VStack spacing={12}>
            {/* Section Title */}
        {/* <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="bold"
              textAlign="center"
              color="#333333"
              fontFamily="'Open Sans', sans-serif"
              textTransform="uppercase"
            >
              Switch to Solar. Keep More Money in Your Pocket.
            </Heading>

            {/* Video Carousel */}
        {/* <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} width="100%">
              {/* Video 1 - Ryan */}
        {/* <Box
                position="relative"
                borderRadius="lg"
                overflow="hidden"
                cursor="pointer"
                bg="gray.100"
                onClick={onVideoOpen}
                _hover={{
                  transform: "scale(1.02)",
                  transition: "transform 0.3s",
                }}
              >
                <Image
                  src="/assets/onesolarimages/preview_images/Landing-Ryan.png"
                  alt="Ryan Video Thumbnail"
                  width="100%"
                  height="auto"
                  objectFit="contain"
                />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  bg="rgba(0, 0, 0, 0.6)"
                  borderRadius="full"
                  p={4}
                  transition="all 0.3s"
                  _hover={{
                    bg: "rgba(0, 0, 0, 0.8)",
                  }}
                >
                  <Icon
                    as={FaPlay}
                    color="white"
                    boxSize={6}
                    ml={1}
                  />
                </Box>
                <Text
                  position="absolute"
                  bottom={4}
                  left={4}
                  color="white"
                  fontWeight="bold"
                  fontSize="lg"
                  fontFamily="'Open Sans', sans-serif"
                >
                  Ryan's Story
                </Text>
              </Box>

              {/* Video 2 - Mel */}
        {/* <Box
                position="relative"
                borderRadius="lg"
                overflow="hidden"
                cursor="pointer"
                bg="gray.100"
                _hover={{
                  transform: "scale(1.02)",
                  transition: "transform 0.3s",
                }}
              >
                <Image
                  src="/assets/onesolarimages/preview_images/Landing-Mel-2.png"
                  alt="Mel Video Thumbnail"
                  width="100%"
                  height="auto"
                  objectFit="contain"
                />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  bg="rgba(0, 0, 0, 0.6)"
                  borderRadius="full"
                  p={4}
                  transition="all 0.3s"
                  _hover={{
                    bg: "rgba(0, 0, 0, 0.8)",
                  }}
                >
                  <Icon
                    as={FaPlay}
                    color="white"
                    boxSize={6}
                    ml={1}
                  />
                </Box>
                <Text
                  position="absolute"
                  bottom={4}
                  left={4}
                  color="white"
                  fontWeight="bold"
                  fontSize="lg"
                  fontFamily="'Open Sans', sans-serif"
                >
                  Mel's Story
                </Text>
              </Box>

              {/* Video 3 - Deik */}
        {/* <Box
                position="relative"
                borderRadius="lg"
                overflow="hidden"
                cursor="pointer"
                bg="gray.100"
                _hover={{
                  transform: "scale(1.02)",
                  transition: "transform 0.3s",
                }}
              >
                <Image
                  src="/assets/onesolarimages/preview_images/OSS-website-Deik.png"
                  alt="Deik Video Thumbnail"
                  width="100%"
                  height="auto"
                  objectFit="contain"
                />
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  bg="rgba(0, 0, 0, 0.6)"
                  borderRadius="full"
                  p={4}
                  transition="all 0.3s"
                  _hover={{
                    bg: "rgba(0, 0, 0, 0.8)",
                  }}
                >
                  <Icon
                    as={FaPlay}
                    color="white"
                    boxSize={6}
                    ml={1}
                  />
                </Box>
                <Text
                  position="absolute"
                  bottom={4}
                  left={4}
                  color="white"
                  fontWeight="bold"
                  fontSize="lg"
                  fontFamily="'Open Sans', sans-serif"
                >
                  Deik's Story
                </Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box> */}

        {/* Background Image Section with White Overlay */}
        <Box
          position="relative"
          width="100%"
          py={{ base: 20, md: 32 }}
          backgroundImage="url('/assets/onesolarimages/preview_images/one-solar-2000-1200-services-background.jpg')"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
        >
          {/* White Overlay - 80% opacity for whiter appearance */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(255, 255, 255, 0.6)"
            zIndex={1}
          />

          {/* Content Container */}
          <Container maxW="1200px" position="relative" zIndex={2}>
            {/* Services Header - Full Width */}
            <VStack spacing={6} align="flex-start" mb={16}>
              {/* Divider */}
              <Box width="60px" height="2px" bg="#121c47" />

              {/* Our Services Label - Bigger */}
              <Text
                fontSize={{ base: "lg", md: "xl" }}
                fontWeight="700"
                textTransform="uppercase"
                letterSpacing="wider"
                color="#121c47"
                fontFamily="'Open Sans', sans-serif"
              >
                OUR SERVICES
              </Text>

              {/* Main Heading - Full Width */}
              <Heading
                as="h2"
                fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                fontWeight="bold"
                color="#050303"
                textTransform="uppercase"
                fontFamily="'Open Sans', sans-serif"
                width="100%"
              >
                Residential and Commercial Solar for Hamilton, Auckland, Wellington, Christchurch, Tauranga, and throughout New Zealand.
              </Heading>

              {/* Description - Full Width */}
              <Text
                fontSize={{ base: "md", md: "lg" }}
                color="#333333"
                fontFamily="'Open Sans', sans-serif"
                fontWeight="bold"
                width="100%"
              >
                We deliver the most efficient, affordable & reliable solar solutions for your home or business.
              </Text>
            </VStack>

            {/* Service Cards */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              {/* Residential Solar Card */}
              <Box
                bg="white"
                overflow="hidden"
                boxShadow="lg"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-8px)",
                  boxShadow: "xl",
                }}
              >
                <Box p={6} bg="white">
                  <Image
                    src="/assets/onesolarimages/services/residential_solar.jpg"
                    alt="Residential Solar"
                    width="100%"
                    height="200px"
                    objectFit="cover"
                  />
                </Box>
                <Box p={6}>
                  <HStack spacing={3} mb={4}>
                    <Box width="3px" height="24px" bg="#121c47" />
                    <Heading
                      as="h3"
                      fontSize="xl"
                      fontWeight="bold"
                      color="#050303"
                      fontFamily="'Open Sans', sans-serif"
                      textTransform="uppercase"
                    >
                      RESIDENTIAL SOLAR
                    </Heading>
                  </HStack>
                  <Text
                    color="#666666"
                    mb={6}
                    fontFamily="'Open Sans', sans-serif"
                  >
                    Solar power can reduce your home electricity bill, earn you money for extra electricity generated and help reduce greenhouse gas emissions.
                  </Text>
                  <Button
                    variant="solid"
                    bg="#121c47"
                    color="white"
                    size="sm"
                    textTransform="uppercase"
                    fontFamily="'Open Sans', sans-serif"
                    width="100%"
                    borderRadius="0"
                    _hover={{
                      bg: "#0a1230",
                    }}
                    onClick={() => navigate("/residential-solar")}
                  >
                    Learn More
                  </Button>
                </Box>
              </Box>

              {/* Commercial Solar Card */}
              <Box
                bg="white"
                overflow="hidden"
                boxShadow="lg"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-8px)",
                  boxShadow: "xl",
                }}
              >
                <Box p={6} bg="white">
                  <Image
                    src="/assets/onesolarimages/services/0000_commercial-solar-padwjfyy4ep358hau42it2wvj14e48kmuk3yq4g0ww.jpg"
                    alt="Commercial Solar"
                    width="100%"
                    height="200px"
                    objectFit="cover"
                  />
                </Box>
                <Box p={6}>
                  <HStack spacing={3} mb={4}>
                    <Box width="3px" height="24px" bg="#121c47" />
                    <Heading
                      as="h3"
                      fontSize="xl"
                      fontWeight="bold"
                      color="#050303"
                      fontFamily="'Open Sans', sans-serif"
                      textTransform="uppercase"
                    >
                      COMMERCIAL SOLAR
                    </Heading>
                  </HStack>
                  <Text
                    color="#666666"
                    mb={6}
                    fontFamily="'Open Sans', sans-serif"
                  >
                    Reduce your commercial electricity costs, and put money back into your business with our quality solar systems.
                  </Text>
                  <Button
                    variant="solid"
                    bg="#121c47"
                    color="white"
                    size="sm"
                    textTransform="uppercase"
                    fontFamily="'Open Sans', sans-serif"
                    width="100%"
                    borderRadius="0"
                    _hover={{
                      bg: "#0a1230",
                    }}
                    onClick={() => navigate("/commercial-solar")}
                  >
                    Learn More
                  </Button>
                </Box>
              </Box>

              {/* Solar Monitoring Card */}
              <Box
                bg="white"
                overflow="hidden"
                boxShadow="lg"
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-8px)",
                  boxShadow: "xl",
                }}
              >
                <Box p={6} bg="white">
                  <Image
                    src="/assets/onesolarimages/services/solarmonitoring.jpg"
                    alt="Solar Monitoring"
                    width="100%"
                    height="200px"
                    objectFit="cover"
                  />
                </Box>
                <Box p={6}>
                  <HStack spacing={3} mb={4}>
                    <Box width="3px" height="24px" bg="#121c47" />
                    <Heading
                      as="h3"
                      fontSize="xl"
                      fontWeight="bold"
                      color="#050303"
                      fontFamily="'Open Sans', sans-serif"
                      textTransform="uppercase"
                    >
                      SOLAR MONITORING
                    </Heading>
                  </HStack>
                  <Text
                    color="#666666"
                    mb={6}
                    fontFamily="'Open Sans', sans-serif"
                  >
                    Solar Analytics shows you exactly when your appliances are using energy so you can see how much you are consuming.
                  </Text>
                  <Button
                    variant="solid"
                    bg="#121c47"
                    color="white"
                    size="sm"
                    textTransform="uppercase"
                    fontFamily="'Open Sans', sans-serif"
                    width="100%"
                    borderRadius="0"
                    _hover={{
                      bg: "#0a1230",
                    }}
                    onClick={() => navigate("/solar-monitoring")}
                  >
                    Learn More
                  </Button>
                </Box>
              </Box>
            </SimpleGrid>
          </Container>
        </Box>

        {/* Partners Section */}
        <Box
          bg="white"
          py={{ base: 16, md: 20 }}
          position="relative"
          overflow="hidden"
        >
          <Container maxW="1200px">
            <VStack spacing={8}>
              {/* Heading */}
              <Heading
                as="h2"
                fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                fontWeight="bold"
                textAlign="center"
                color="#050303"
                fontFamily="'Open Sans', sans-serif"
              >
                Partnered with New Zealand's Most Trusted Solar Brands
              </Heading>

              {/* Divider */}
              <Box width="60px" height="2px" bg="#121c47" />
            </VStack>
          </Container>

          {/* Brand Logos Carousel - Full Width */}
          <Box
            position="relative"
            width="100%"
            mt={12}
            overflow="hidden"
          >
            <style>
              {`
              @keyframes scroll {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-50%);
                }
              }
              .partners-carousel {
                display: flex;
                animation: scroll 20s linear infinite;
                width: fit-content;
              }
              .partners-carousel:hover {
                animation-play-state: paused;
              }
            `}
            </style>
            <Box className="partners-carousel">
              {/* First set of logos */}
              <HStack spacing={20} px={10}>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/sungrow.jpg" alt="Sungrow" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/solar-analytics.png.webp" alt="Solar Analytics" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/risen.png.webp" alt="Risen" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/Q-cells.png.webp" alt="Q-Cells" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/lg-chem.png.webp" alt="LG Chem" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/fronius.png.webp" alt="Fronius" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/aus-gov.png.webp" alt="Australian Government" maxH="140px" objectFit="contain" />
                </Box>
              </HStack>

              {/* Duplicate set for seamless loop */}
              <HStack spacing={20} px={10}>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/sungrow.jpg" alt="Sungrow" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/solar-analytics.png.webp" alt="Solar Analytics" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/risen.png.webp" alt="Risen" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/Q-cells.png.webp" alt="Q-Cells" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/lg-chem.png.webp" alt="LG Chem" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/fronius.png.webp" alt="Fronius" maxH="140px" objectFit="contain" />
                </Box>
                <Box minW="280px" display="flex" alignItems="center" justifyContent="center" height="160px">
                  <Image src="/assets/onesolarimages/partners/aus-gov.png.webp" alt="Australian Government" maxH="140px" objectFit="contain" />
                </Box>
              </HStack>
            </Box>
          </Box>
        </Box>

        {/* New Section with Mum and Baby Background */}
        <Box
          position="relative"
          width="100%"
          minHeight={{ base: "400px", md: "500px" }}
          backgroundImage="url('/assets/onesolarimages/mumwithbaby.jpg')"
          backgroundSize="cover"
          backgroundPosition="center"
          backgroundRepeat="no-repeat"
        >
          {/* Dark overlay - more transparent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="rgba(18, 28, 71, 0.5)"
          />

          {/* Content */}
          <Container
            maxW="1200px"
            position="relative"
            zIndex={2}
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={{ base: 16, md: 20 }}
          >
            <Box maxW="900px" textAlign="center">
              <Heading
                as="h1"
                fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
                fontWeight="bold"
                color="white"
                fontFamily="'Open Sans', sans-serif"
                lineHeight="1.2"
                textTransform="uppercase"
              >
                With 10+ Years of Experience, One Solar is the Smart Choice in Solar.
              </Heading>
            </Box>
          </Container>
        </Box>

        {/* Video Modal */}
        <Modal isOpen={isVideoOpen} onClose={onVideoClose} size="full">
          <ModalOverlay bg="#121c47" opacity={0.95} />
          <ModalContent
            bg="transparent"
            maxW="100vw"
            m={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100vh"
          >
            <ModalCloseButton
              color="white"
              size="lg"
              top={8}
              right={8}
              zIndex={10}
              bg="rgba(255, 255, 255, 0.1)"
              borderRadius="full"
              _hover={{
                bg: "rgba(255, 255, 255, 0.2)",
              }}
            />
            <ModalBody
              p={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              height="100%"
            >
              <Box
                position="relative"
                width="90%"
                maxW="1200px"
                aspectRatio="16/9"
              >
                <Box
                  as="iframe"
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/lMWgA1VoINY?autoplay=1&controls=0&rel=0"
                  title="Ryan's Solar Story"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  borderRadius="lg"
                />
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>



        <FooterWithFourColumns />

        {/* Floating Voice Call Button */}
        <VapiFloatingCallButton />
      </Box>
    </>
  );
};

export default Home; 